import { useCallback, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import {
  swapConversation,
  setCreatingNewConversation,
} from '@/redux/slices/copilotKitChatSlice';
import { useNewConversationMutation } from '@/redux/slices/gigawattBackendApiSlice';
import { clearAllFeedback } from '@/redux/slices/messageFeedbackSlice';
import { CopilotKitAgentContext } from '@/components/CopilotKitProvider/CopilotKitAgentContext';
import { CHAT_NAMESPACES } from '@/types/aiChat';
import { useGlobalUI } from '@/components/GlobalUIProvider';

type UseNewChatActionResult = {
  handleNewChat: () => Promise<boolean>;
  isDisabled: boolean;
};

/**
 * Hook that provides a function to start a new chat conversation
 *
 * Fires the API first and only swaps state on success (atomic swap).
 * This prevents conversationId from ever passing through undefined, which would
 * put CopilotKitProvider into a loading state and blank the app.
 *
 * The button is automatically disabled when a message is loading or a new
 * conversation is being created.
 *
 * @param namespaceKey - The namespace key for the chat to clear
 * @param externalIsLoading - Optional loading state passed from parent
 * @returns Object with handleNewChat function and isDisabled state
 *
 * @example
 * ```tsx
 * const { handleNewChat, isDisabled } = useNewChatAction(CHAT_NAMESPACES.GIGAWATT_COPILOTKIT, isLoading);
 *
 * // In a button handler:
 * <Button onClick={handleNewChat} disabled={isDisabled}>New Chat</Button>
 * ```
 */
export const useNewChatAction = (
  namespaceKey: string = CHAT_NAMESPACES.GIGAWATT_COPILOTKIT,
  externalIsLoading?: boolean
): UseNewChatActionResult => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('ai-chat');
  const { showErrorToast } = useGlobalUI();

  const organizationId = useAppSelector(
    state => state.loggedUser?.organizationId
  );

  const copilotKitConversation = useAppSelector(
    state => state.copilotKitChat.conversations[namespaceKey]
  );

  const isLoading = externalIsLoading ?? false;

  // Redux-driven flag set synchronously at the start of a new-chat API call.
  // Unlike RTK Query's isCreatingConversation (which only flips after newConversation()
  // is invoked), this flag is set before the simulated delay and any async work,
  // so the button disables and the spinner appears immediately on click.
  const isCreatingNewConversation =
    copilotKitConversation?.isCreatingNewConversation ?? false;

  const [newConversation, { isLoading: isCreatingConversation }] =
    useNewConversationMutation();

  const newChatFailedMessage = t('errors.newChatFailed');

  // Extract only setMessages — depending on the whole context object would cause
  // handleNewChat to recreate on every streaming token as messages accumulate.
  const setMessages = useContext(CopilotKitAgentContext)?.setMessages;

  // Two guards work together to prevent concurrent new-chat calls:
  //   isNewChatInFlightRef — synchronous; blocks a second click before React re-renders.
  //   isCreatingConversation — async RTK Query flag; blocks calls after a re-render has
  //     occurred but while the network request is still in-flight (e.g. the ref was reset
  //     due to an unexpected error but the mutation hasn't settled yet).
  // isCreatingNewConversation (Redux) is a UI-only flag used for the spinner and disabled
  // state; it is not part of the early-return guard.
  const isNewChatInFlightRef = useRef(false);

  const handleNewChat = useCallback(async () => {
    // Don't clear if currently processing
    if (isLoading || isCreatingConversation || isNewChatInFlightRef.current)
      return false;

    // Atomic swap: fire the API first, only update state on success.
    // This keeps conversationId from ever passing through undefined.
    if (!organizationId) return false;

    // Set the ref immediately (synchronous) to block any concurrent invocation.
    // Dispatch to Redux to update the spinner in the messages area right away.
    isNewChatInFlightRef.current = true;
    dispatch(setCreatingNewConversation({ namespaceKey, value: true }));

    try {
      const result = await newConversation(organizationId).unwrap();
      dispatch(
        swapConversation({
          namespaceKey,
          conversationId: result.conversationId,
        })
      );
      dispatch(clearAllFeedback());
      // Clear CopilotKit's in-memory messages directly so the UI resets
      // immediately. CopilotKit does not clear messages on threadId change —
      // it only reconnects to the new thread. Hydration then shows the
      // welcome message once messages are empty.
      setMessages?.([]);
      return true;
    } catch (error) {
      // On failure, keep existing state — don't blank the chat
      window.DD_RUM?.addError(error, {
        location: 'useNewChatAction - handleNewChat',
      });
      showErrorToast({
        id: 'new-chat-error-toast',
        message: newChatFailedMessage,
      });
      return false;
    } finally {
      isNewChatInFlightRef.current = false;
      dispatch(setCreatingNewConversation({ namespaceKey, value: false }));
    }
  }, [
    dispatch,
    showErrorToast,
    namespaceKey,
    isLoading,
    isCreatingConversation,
    organizationId,
    newConversation,
    newChatFailedMessage,
    setMessages,
  ]);

  return {
    handleNewChat,
    isDisabled:
      isLoading || isCreatingConversation || isCreatingNewConversation,
  };
};
